import "./App.css";
import "./pages/SubmitJobs/ImportForm.css";
import { Fade } from "@mui/material";
import React from "react";
import { CustomToolbar, DrawerNavigation, SIDEBAR_COLLAPSED_WIDTH } from "./widgets/Toolbar";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Sentry from "@sentry/react";
import { LoadingComponent } from "@gui/widgets/LoadingComponent";
import { PageErrorBoundary } from "@gui/widgets/SectionErrorBoundary";
import { User } from "@domain/Auth";
import { useCurrentUser } from "./useCurrentUser";
import { AppProviders } from "./providers/AppProviders";
import { useResponsiveView } from "@gui/hooks/useResponsiveView";
import { AppRoutes } from "./routes/AppRoutes";

function App() {
	// User identity + load state managed by useCurrentUser. The hook owns the
	// auth-failure interceptor wiring, the user fetch, and Sentry user reporting.
	const { currentUser, loading, setCurrentUser, setLoading } = useCurrentUser();
	const [mobileOpen, setMobileOpen] = React.useState(false);
	const { isMobile } = useResponsiveView();

	const handleLogin = (user: User) => {
		setCurrentUser(user);
	};

	// ToastContainer mounts unconditionally (outside the loading ternary below) so it's
	// never racing its own first-mount queue flush against the initial user-fetch's
	// rejection — see #645 (dropped "Session expired" toast under StrictMode double-invoke).
	return (
		<>
			{loading ? (
				<div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100dvh" }}>
					<LoadingComponent />
				</div>
			) : (
				<Fade in={!loading}>
					<div>
						<AppProviders currentUser={currentUser}>
							{/* dvh, not vh: iOS browsers (incl. Chrome on iPad, which is WebKit) size 100vh
							    as if their toolbar were collapsed, so a 100vh shell overflows the visible
							    area and the whole page — header included — scrolls under the fixed sidebar. */}
							<div
								style={{
									height: "100dvh",
									overflow: "hidden",
									display: "flex",
									flexDirection: "column",
								}}
							>
								<CustomToolbar onMenuClick={() => setMobileOpen(true)} />
								<DrawerNavigation mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />

								{/* Route container flex-fills the remaining viewport height after the top toolbar. The
						    previous `calc(100vh - 64px)` assumed a single-row 64px Toolbar, but on narrow
						    mobile widths the top toolbar wraps to two rows (~96px), which clipped the bottom
						    32px of every page. Using flex: 1 + minHeight: 0 adapts to whatever the
						    toolbar's natural height is. */}
								<div
									style={{
										marginLeft: isMobile ? 0 : SIDEBAR_COLLAPSED_WIDTH,
										flex: 1,
										minHeight: 0,
										overflowY: "auto",
									}}
								>
									<PageErrorBoundary>
										<AppRoutes onLogin={handleLogin} setLoading={setLoading} />
									</PageErrorBoundary>
								</div>
							</div>
						</AppProviders>
					</div>
				</Fade>
			)}
			{/* Top on a phone: at 480px and below toasts span the full width, and at the bottom they
			    would cover a page's bottom toolbar (Save/Create) until dismissed. */}
			<ToastContainer position={isMobile ? "top-center" : "bottom-left"} />
		</>
	);
}

export default Sentry.withProfiler(App);
